import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { HomeStateData } from '@workspace/api-backend'
import {
  v2GetStartupHomeStateOptions,
  v2GetStartupHomeStateQueryKey,
  v2SetDistributionIntentMutation,
  v2SnoozeShareStepMutation,
} from '@workspace/api-backend/react-query'
import { useCallback } from 'react'

/**
 * States that resolve themselves without the founder doing anything — a deck
 * being read, a matcher running, a sign-in link being minted. They need to
 * advance on their own or the founder sits on a spinner until they reload.
 */
const SELF_RESOLVING: ReadonlySet<HomeStateData['key']> = new Set([
  'QUEST_READING_DECK',
  'QUEST_FINDING_INVESTORS',
  'QUEST_PREPARING_LINK',
])

const POLL_MS = 15_000

/**
 * Steady-state refresh. Most things that move Home invalidate it directly, but
 * plenty happens server-side that no click here can know about — a send going
 * out, a reply landing, the budget pause firing. A minute keeps Home and the
 * sidebar CTA honest without the founder ever reaching for reload.
 */
const IDLE_POLL_MS = 60_000

/**
 * Long enough that moving between founder pages reuses the cache (the sidebar
 * CTA reads this on every one of them), short enough that a state which
 * changed elsewhere is not stale for long.
 */
const STALE_MS = 60_000

export const homeStateQueryKey = (startupId: string) =>
  v2GetStartupHomeStateQueryKey({ path: { startupId } })

/**
 * The founder's current Home state.
 *
 * Deliberately one query for both the focus card and the sidebar CTA: they
 * share this key, so on Home a single request serves both and a button can
 * never point somewhere other than the card beside it.
 */
export function useHomeStateQuery(startupId: string, options?: { enabled?: boolean }) {
  return useQuery({
    ...v2GetStartupHomeStateOptions({ path: { startupId } }),
    enabled: options?.enabled ?? true,
    staleTime: STALE_MS,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const key = query.state.data?.data?.key
      return key && SELF_RESOLVING.has(key) ? POLL_MS : IDLE_POLL_MS
    },
  })
}

/**
 * Invalidate the home state after an action that could change it.
 *
 * Worth using rather than reaching for the key directly: outreach alone has
 * more than twenty mutation sites, and the card and the sidebar both read this,
 * so one invalidation has to update both.
 */
export function useInvalidateHomeState(startupId: string) {
  const queryClient = useQueryClient()
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: homeStateQueryKey(startupId) }),
    [queryClient, startupId]
  )
}

/**
 * Sets which path the founder is on for reaching investors. Called by the
 * onboarding lane-picker overlay (lane click or skip) and by the "escape to
 * share" ghost on early outreach cards.
 */
export function useSetDistributionIntentMutation(startupId: string) {
  const invalidateHome = useInvalidateHomeState(startupId)
  return useMutation({
    ...v2SetDistributionIntentMutation({ path: { startupId } }),
    onSuccess: () => invalidateHome(),
  })
}

/** "I'll do this later" on the Share card — snoozes it for 7 days. */
export function useSnoozeShareStepMutation(startupId: string) {
  const invalidateHome = useInvalidateHomeState(startupId)
  return useMutation({
    ...v2SnoozeShareStepMutation({ path: { startupId } }),
    onSuccess: () => invalidateHome(),
  })
}

export type { HomeStateData }
